import { TouchKit, TouchKitEventType, type ITouchKitEventCallback } from '@/utils/event-kit';
import { PluginType, type IExportPlugin } from '@/version-types';
import { ServiceManager } from '../services';
import type { MoveListenerLevel } from '../shared';
import { InstanceData, InstanceDataType } from './instance-data';
import identityState from './states/identity-state';

export const identityStatePlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.EXTENSION,
    name: 'framework-core',
    createState() {
        return [identityState];
    },
};

export interface IFramework {
    identity: string;
    service: ServiceManager;
}

export class Framework {
    protected identity: string;
    protected framework: IFramework;

    constructor(identity: string) {
        this.identity = identity;
        this.framework = getFramework(identity);
    }
}

function createFramework(identity: string) {
    const instance = InstanceData.getData(identity, InstanceDataType.Framework);
    if (!instance) {
        const service = new ServiceManager(identity);
        const tk = new TouchKit();
        InstanceData.setData(identity, InstanceDataType.TouchListenerList, []);
        tk.on(TouchKitEventType.Move, (e) => {
            if (service.editor.disableOperate) return;
            const tkListenerList = InstanceData.getData(identity, InstanceDataType.TouchListenerList) as {
                name: string;
                level: MoveListenerLevel;
                listenerRef: { current: ITouchKitEventCallback };
            }[];
            const listenerList = tkListenerList
                .sort((a, b) => b.level - a.level)
                .map((item) => item.listenerRef.current);
            listenerList.forEach((listener) => {
                listener(e);
            });
        });
        InstanceData.setData(identity, InstanceDataType.TouchData, tk);
        InstanceData.onDestroy(identity, (identity) => {
            const tk = InstanceData.getData(identity, InstanceDataType.TouchData) as TouchKit;
            if (tk) {
                tk.off(TouchKitEventType.Move);
            }
        });
        service.registration.registerPlugins([{ plugin: identityStatePlugin }]);
        const framework = {
            identity,
            service,
        };
        InstanceData.setData(identity, InstanceDataType.Framework, framework);
    }
}

export const getFramework = (identity: string): IFramework => {
    const instance = InstanceData.getData(identity, InstanceDataType.Framework);
    if (!instance) {
        createFramework(identity);
    }
    return InstanceData.getData(identity, InstanceDataType.Framework);
};

/** 延迟删除任务：同 identity 重建 Editor 时需取消，否则会把新实例的 framework 清掉 */
const pendingFrameworkDeletes = new Map<string, { timer: ReturnType<typeof setTimeout>; framework: unknown }>();

/**
 * 取消指定 identity 的延迟删除，并同步清掉旧 framework
 *
 * 用于同 id 单例编辑器（如消除笔弹窗）在 destroy 后立刻 recreate 的场景，
 * 避免 setTimeout(100ms) 误删新实例。
 *
 * @param identity - 编辑器 identity
 */
export function resetFramework(identity: string) {
    const pending = pendingFrameworkDeletes.get(identity);
    if (pending) {
        clearTimeout(pending.timer);
        pendingFrameworkDeletes.delete(identity);
    }
    if (InstanceData.getData(identity, InstanceDataType.Framework)) {
        InstanceData.delInstance(identity);
    }
}

/**
 * 延迟删除 framework，避免销毁当下仍有异步回调在用
 *
 * 仅当超时后仍是「销毁时」的那份 framework 引用时才真正删除；
 * 若期间已 reset / 重建同 identity 的新实例则跳过。
 *
 * @param identity - 编辑器 identity
 */
export function delFramework(identity: string) {
    const framework = InstanceData.getData(identity, InstanceDataType.Framework);
    const pending = pendingFrameworkDeletes.get(identity);
    if (pending) {
        clearTimeout(pending.timer);
    }

    const timer = setTimeout(() => {
        pendingFrameworkDeletes.delete(identity);
        // 同 id 已重建时 framework 引用会变化，不能删新实例
        if (InstanceData.getData(identity, InstanceDataType.Framework) === framework) {
            InstanceData.delInstance(identity);
        }
    }, 100);

    pendingFrameworkDeletes.set(identity, { timer, framework });
}
